
import "es6-promise";

import filter from "lodash-es/filter";
import map from "lodash-es/map";
import head from "lodash-es/head";
import sortBy from "lodash-es/sortBy";

import { Memoize, Debounce } from "lodash-decorators";

import axios, { AxiosResponse } from "axios";
import Vue, { ComponentOptions } from "vue";
import Component from "vue-class-component";
import {
  Emit,
  Inject,
  Model,
  Prop,
  Provide,
  Watch,
} from "vue-property-decorator";

import { State, Getter, Action, Mutation, namespace } from "vuex-class";

import { ComputeJob, LaxyFile } from "../model";
import { WebAPI } from "../web-api";
import { FETCH_FILESET } from "../store";
import { strToRegex } from "../util";
import {
  hasSharedTagOrEmpty,
  hasIntersection,
  filterByTag,
  excludeByTag,
  filterByRegex,
  filterByFullPath,
  filterByFilename,
  viewFile,
  downloadFile,
} from "../file-tree-util";

// import {DummyFileSet as _dummyFileSet} from "../test-data";
import { Snackbar } from "../snackbar";
import { ViewMethod } from "../types";

@Component({
  filters: {},
})
export default class FileList extends Vue {
  _DEBUG: boolean = false;

  @Prop(String)
  public filesetId: string;

  @Prop(String)
  public title: string;

  @Prop({
    type: Array,
    default: () => {
      return [];
    },
  })
  public regexFilters: string[];

  @Prop({
    type: Array,
    default: () => {
      return [];
    },
  })
  public tagFilters: string[];

  @Prop({
    type: Array,
    default: () => {
      return [];
    },
  })
  public excludeTags: string[];

  @Prop({ default: true })
  public hideSearch: boolean;

  @Prop({ default: 2, type: Number })
  public minQueryLength: number;

  @Prop(String)
  public jobId: string | null;

  @Prop({ type: Boolean, default: true })
  public refreshOnLoad: boolean;

  public searchQuery: string = "";
  public searching: boolean = false;

  public actionRunning: any = {};

  get fileset(): any {
    // this appears reactive. reading directly
    return this.$store.getters.fileset(this.filesetId);
  }

  private viewMethods: ViewMethod[] = [
    {
      text: "Open in new tab",
      icon: "open_in_new",
      tags: [],
      method: (file_id: string) => {
        viewFile(file_id, this.fileset, this.jobId);
      },
    },
    {
      text: "Download file",
      icon: "cloud_download",
      tags: [],
      method: (file_id: string) => {
        downloadFile(file_id, this.fileset, this.jobId);
      },
    },
    {
      text: "View report",
      icon: "remove_red_eye",
      tags: ["html", "report"],
      method: (file_id: string) => {
        viewFile(file_id, this.fileset, this.jobId);
      },
    },
    {
      text: "Open in Degust",
      icon: "dashboard",
      tags: ["degust"],
      method: async (file: LaxyFile) => {
        window.open(
          WebAPI.getExternalAppRedirectUrl("degust", file.id),
          "_blank"
        );
      },
    },
  ];

  public refreshing: boolean = false;

  mounted() {
    // this.files = _dummyFileSet;
    // this.filesetId = _dummyFileSet.id;
    if (this.refreshOnLoad) this.refresh();
  }

  // we need this wrapped in a method otherwise the viewMethod.method
  // doesn't have the correct 'this' context to $emit events.
  emitActionError(msg: string) {
    this.$emit("action-error", msg);
  }

  getViewMethodsForTags(tags: string[]) {
    return filter(this.viewMethods, (vm) => hasSharedTagOrEmpty(vm.tags, tags));
  }

  @Memoize((file: LaxyFile) => file.id)
  getDefaultViewMethod(file: LaxyFile) {
    return head(
      filter(this.viewMethods, (vm) => hasIntersection(vm.tags, file.type_tags))
    );
  }

  @Debounce(600)
  get files(): LaxyFile[] {
    const fileset = this.fileset;
    if (
      fileset == null ||
      fileset.files == null ||
      fileset.files.length === 0
    ) {
      return [];
    }

    let filtered: LaxyFile[] = fileset.files;
    filtered = filterByTag(filtered, this.tagFilters);
    filtered = filterByRegex(filtered, strToRegex(this.regexFilters));
    filtered = excludeByTag(filtered, this.excludeTags)

    const query = this.searchQuery.trim();
    if (query.length >= this.minQueryLength) {
      filtered = filterByFilename(filtered, query, false);
    }
    // const filtered = filter(this.fileset.files, (f) => {
    //     some(strToRegex(this.regexFilters), (p) => {f.name.matches(p)});
    // });
    filtered = sortBy(filtered, ["path", "name"]);
    return filtered;
  }

  get titleText(): string {
    return this.title == null ? this.fileset.name : this.title;
  }

  async refresh(force: boolean = false) {
    if (this.refreshing && !force) return;

    try {
      // const response = await WebAPI.getFileSet(this.filesetId);
      // this.fileset = response.data as LaxyFileSet;
      if (this.filesetId && (force || this.fileset == null)) {
        this.refreshing = true;
        await this.$store.dispatch(FETCH_FILESET, this.filesetId);
        this.$emit("refresh-success", "Updated !");
        this.refreshing = false;
      }
    } catch (error) {
      console.log(error);
      this.refreshing = false;
      this.$emit(
        "refresh-error",
        error.toString() + ` (filesetId: ${this.filesetId})`
      );
      throw error;
    }
  }
}
